import { useEffect, useState } from 'react';
import { versionsAPI, type Version } from '../services/api';

export default function Versions() {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadVersions();
  }, []);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const data = await versionsAPI.getAll();
      setVersions(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVersion = async () => {
    const description = prompt('Enter version description:');
    if (!description) return;

    try {
      await versionsAPI.create(description);
      await loadVersions();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleActivate = async (id: number) => {
    if (!confirm('Activate this version?')) return;

    try {
      await versionsAPI.activate(id);
      await loadVersions();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (error) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <div className="text-red-600 font-semibold mb-2">Error: {error}</div>
          <p className="text-sm text-gray-600 mt-4">
            Make sure the backend server is running on port 8787.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Versions</h1>
          <p className="mt-2 text-gray-600">Manage dictionary versions and snapshots</p>
        </div>
        <button onClick={handleCreateVersion} className="btn btn-primary">
          Create Version
        </button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Description</th>
              <th>Created By</th>
              <th>Created At</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((version) => (
              <tr key={version.id}>
                <td>v{version.version_number}</td>
                <td>{version.description || '-'}</td>
                <td>{version.created_by || '-'}</td>
                <td>{new Date(version.created_at).toLocaleDateString()}</td>
                <td>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      version.is_active === 1
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {version.is_active === 1 ? 'Active' : 'Inactive'}
                  </span>
                  {version.is_approved === 1 && (
                    <span className="ml-2 px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      Approved
                    </span>
                  )}
                </td>
                <td>
                  <button
                    onClick={() => handleActivate(version.id)}
                    className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                    disabled={version.is_active === 1}
                  >
                    {version.is_active === 1 ? 'Active' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

